import { DownloadOutlined } from "@ant-design/icons";
import { IYoutubeScrapeResponseItem } from "@shared/lib/types/influencer";
import { Button, Popover, Typography } from "@shared/ui";
import { Checkbox } from "@shared/ui/Checkbox";
import { useMemo, useState } from "react";
import {
  ALL_YOUTUBE_CSV_COLUMN_KEYS,
  YOUTUBE_CSV_COLUMNS,
  YoutubeCsvColumnKey,
} from "../lib/csvColumns";
import { exportYoutubeSearchCsv } from "../lib/exportYoutubeSearchCsv";
import styles from "./ExportCsvButton.module.scss";

const { Text } = Typography;

type ExportCsvButtonProps = {
  items: IYoutubeScrapeResponseItem[];
};

export function ExportCsvButton({ items }: ExportCsvButtonProps) {
  const [open, setOpen] = useState(false);
  const [selectedKeys, setSelectedKeys] = useState<YoutubeCsvColumnKey[]>(
    ALL_YOUTUBE_CSV_COLUMN_KEYS
  );

  const allSelected = selectedKeys.length === ALL_YOUTUBE_CSV_COLUMN_KEYS.length;
  const someSelected = selectedKeys.length > 0 && !allSelected;

  const selectedKeySet = useMemo(
    () => new Set(selectedKeys),
    [selectedKeys]
  );

  const handleToggleAll = (checked: boolean) => {
    setSelectedKeys(checked ? ALL_YOUTUBE_CSV_COLUMN_KEYS : []);
  };

  const handleToggleColumn = (key: YoutubeCsvColumnKey, checked: boolean) => {
    setSelectedKeys((current) =>
      checked ? [...current, key] : current.filter((item) => item !== key)
    );
  };

  const handleDownload = () => {
    exportYoutubeSearchCsv(items, selectedKeys);
    setOpen(false);
  };

  return (
    <Popover
      trigger="click"
      open={open}
      onOpenChange={setOpen}
      placement="bottomRight"
      content={
        <div className={styles.panel}>
          <Text className={styles.panelTitle}>Columns to export</Text>
          <Checkbox
            checked={allSelected}
            indeterminate={someSelected}
            onChange={(event) => handleToggleAll(event.target.checked)}
          >
            Select all
          </Checkbox>
          <div className={styles.columnList}>
            {YOUTUBE_CSV_COLUMNS.map((column) => (
              <Checkbox
                key={column.key}
                checked={selectedKeySet.has(column.key)}
                onChange={(event) =>
                  handleToggleColumn(column.key, event.target.checked)
                }
              >
                {column.label}
              </Checkbox>
            ))}
          </div>
          <Button
            type="primary"
            icon={<DownloadOutlined />}
            onClick={handleDownload}
            disabled={items.length === 0 || selectedKeys.length === 0}
            className={styles.downloadButton}
          >
            Download CSV
          </Button>
        </div>
      }
    >
      <Button icon={<DownloadOutlined />} disabled={items.length === 0}>
        Export CSV
      </Button>
    </Popover>
  );
}
